import React, { useEffect, useState, useRef } from 'react';
import { inputStyle } from '../../components/FormField';
import FormField from '../../components/FormField';
import Button from '../../components/Button';
import Modal from '../../components/Modal';
import { contentApi } from '../../services/api';

// ── Types ─────────────────────────────────────────────────────────────────────

type TranscodeStatus = 'Pending' | 'Processing' | 'Complete' | 'Failed';

interface ContentItem {
    id: string;
    type: string;
    title: string;
    durationSeconds: number;
    weightSeconds: number;
    isActive: boolean;
    activeFrom?: string;
    activeTo?: string;
    transcodeStatus: TranscodeStatus;
    fileSizeBytes?: number;
    createdAt: string;
    impressions30d: number;
}

interface AdvertsTabProps {
    clubId: string;
    clubSlug: string;
    isSuperUser: boolean;
    sponsors: { id: string; name: string }[];
}

type AdType = 'LocalAd' | 'SponsorAd' | 'BeautyLoop' | 'ClubNotice' | 'PlatformAd';
type NoticeMode = 'file' | 'text';

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatBytes(bytes?: number): string {
    if (!bytes) return '';
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function statusBadge(status: string): React.CSSProperties {
    switch (status) {
        case 'Complete':   return { background: '#e8f5ee', color: '#1a6b3c' };
        case 'Pending':    return { background: '#fff8e1', color: '#b45309' };
        case 'Processing': return { background: '#e8f0ff', color: '#3b5bdb' };
        case 'Failed':     return { background: '#fff0f0', color: '#c00' };
        default:           return { background: '#f5f5f5', color: '#888' };
    }
}

// ── Upload Form ───────────────────────────────────────────────────────────────

function UploadForm({ clubId, adType, sponsors, onUploaded, onClose }: {
    clubId: string | null;
    adType: AdType;
    sponsors?: { id: string; name: string }[];
    onUploaded: () => void;
    onClose: () => void;
}) {
    const [title, setTitle]           = useState('');
    const [file, setFile]             = useState<File | null>(null);
    const [textBody, setTextBody]     = useState('');
    const [noticeMode, setNoticeMode] = useState<NoticeMode>('file');
    const [duration, setDuration]     = useState(15);
    const [weight, setWeight]         = useState(60);
    const [activeFrom, setActiveFrom] = useState('');
    const [activeTo, setActiveTo]     = useState('');
    const [sponsorId, setSponsorId]   = useState('');
    const [uploading, setUploading]   = useState(false);
    const [progress, setProgress]     = useState(0);
    const [error, setError]           = useState('');
    const fileRef = useRef<HTMLInputElement>(null);

    const isNotice    = adType === 'ClubNotice';
    const isBeauty    = adType === 'BeautyLoop';
    const acceptTypes = isNotice
        ? 'video/mp4,image/jpeg,image/png,image/gif,application/pdf'
        : 'video/mp4,video/quicktime,video/x-msvideo';

    const handleSubmit = async () => {
        if (!title.trim()) { setError('Title is required'); return; }
        if (isNotice && noticeMode === 'text' && !textBody.trim()) {
            setError('Notification text is required'); return;
        }
        if (noticeMode === 'file' && !file) {
            setError('Please select a file'); return;
        }

        setUploading(true);
        setError('');
        setProgress(0);

        try {
            const formData = new FormData();
            if (clubId) formData.append('clubId', clubId);
            formData.append('type', adType);
            formData.append('title', title.trim());
            formData.append('durationSeconds', String(duration));
            formData.append('weightSeconds', String(weight));
            if (activeFrom) formData.append('activeFrom', activeFrom);
            if (activeTo)   formData.append('activeTo', activeTo);
if (file)       formData.append('file', file);
            if (isNotice && noticeMode === 'text')
                formData.append('textBody', textBody.trim());
            if (adType === 'SponsorAd' && sponsorId)
                formData.append('sponsorId', sponsorId);

            await contentApi.upload(formData, (pct) => setProgress(pct));
            onUploaded();
        } catch (e: any) {
            setError(e.message ?? 'Upload failed');
        } finally {
            setUploading(false);
        }
    };

    return (
        <>
            {error && <div style={adStyles.error}>{error}</div>}

            <FormField label="Title">
                <input style={inputStyle} value={title}
                    onChange={e => setTitle(e.target.value)}
                    placeholder={isNotice ? 'e.g. Club Closure Notice' : 'e.g. Pro Shop Summer Sale'}
                    autoFocus />
            </FormField>

            {/* Notice mode selector */}
            {isNotice && (
                <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                    {(['file', 'text'] as NoticeMode[]).map(m => (
                        <button key={m} type="button"
                            onClick={() => setNoticeMode(m)}
                            style={{
                                padding: '6px 16px', borderRadius: 6, fontSize: 13,
                                border: noticeMode === m ? '1px solid #1a6b3c' : '1px solid #ccc',
                                background: noticeMode === m ? '#1a6b3c' : '#fff',
                                color: noticeMode === m ? '#fff' : '#555',
                                cursor: 'pointer', fontWeight: 600,
                            }}>
                            {m === 'file' ? '📎 Upload Image / PDF' : '✏️ Quick Text Notice'}
                        </button>
                    ))}
                </div>
            )}

            {/* File upload */}
            {(!isNotice || noticeMode === 'file') && (
                <FormField
                    label={isNotice ? 'Image or PDF' : isBeauty ? 'Video (MP4)' : 'Video (MP4)'}
                    hint={isNotice
                        ? 'JPG, PNG, GIF or PDF. Portrait images will be letterboxed to landscape.'
                        : 'MP4, MOV up to 500MB. Will be transcoded automatically.'}>
                    <div
                        style={{
                            ...adStyles.dropZone,
                            borderColor: file ? '#1a6b3c' : '#ccc',
                            background: file ? '#f0f7f3' : '#fafafa',
                        }}
                        onDragOver={e => e.preventDefault()}
                        onDrop={e => {
                            e.preventDefault();
                            const f = e.dataTransfer.files[0];
                            if (f) setFile(f);
                        }}
                        onClick={() => fileRef.current?.click()}
                    >
                        {file ? (
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: 14, fontWeight: 600, color: '#1a6b3c' }}>
                                    {file.name}
                                </div>
                                <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
                                    {formatBytes(file.size)}
                                </div>
                            </div>
                        ) : (
                            <div style={{ textAlign: 'center', color: '#888' }}>
                                <div style={{ fontSize: 24, marginBottom: 8 }}>📁</div>
                                <div style={{ fontSize: 13 }}>
                                    Drag & drop or click to select
                                </div>
                            </div>
                        )}
                        <input ref={fileRef} type="file" accept={acceptTypes}
                            style={{ display: 'none' }}
                            onChange={e => setFile(e.target.files?.[0] ?? null)} />
                    </div>
                </FormField>
            )}

            {/* Text notice */}
            {isNotice && noticeMode === 'text' && (
                <FormField label="Notification Text"
                    hint="Displayed as white text on a dark background. Keep it brief.">
                    <textarea style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }}
                        value={textBody}
                        onChange={e => setTextBody(e.target.value)}
                        placeholder="e.g. Course closed this Saturday for maintenance" />
                </FormField>
            )}

            {/* Duration (image/text only — video duration is probed automatically) */}
            {(isNotice || (file && !file.type.startsWith('video/'))) && (
                <FormField label="Display Duration (seconds)"
                    hint="How long this content plays before moving on">
                    <input style={inputStyle} type="number" value={duration}
                        onChange={e => setDuration(parseInt(e.target.value) || 15)}
                        min={5} max={120} />
                </FormField>
            )}

            {/* Sponsor link — only for SponsorAd */}
            {adType === 'SponsorAd' && sponsors && sponsors.length > 0 && (
                <FormField label="Link to Sponsor"
                    hint="Ad will only play during competitions where this sponsor is assigned. Leave blank to play in all competitions.">
                    <select style={inputStyle} value={sponsorId}
                        onChange={e => setSponsorId(e.target.value)}>
                        <option value="">— All competitions (no specific sponsor) —</option>
                        {sponsors.map(s => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                    </select>
                </FormField>
            )}

            {/* Weight — not shown for BeautyLoop (only one plays) */}
            {!isBeauty && (
                <FormField label="Playback Weight"
                    hint="Higher weight = plays more often relative to other ads. Default 60.">
                    <input style={inputStyle} type="number" value={weight}
                        onChange={e => setWeight(parseInt(e.target.value) || 60)}
                        min={1} max={300} />
                </FormField>
            )}

            {/* Active dates */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <FormField label="Active From" hint="Leave blank = always active">
                    <input style={inputStyle} type="date" value={activeFrom}
                        onChange={e => setActiveFrom(e.target.value)} />
                </FormField>
                <FormField label="Active To">
                    <input style={inputStyle} type="date" value={activeTo}
                        onChange={e => setActiveTo(e.target.value)} />
                </FormField>
            </div>

            {/* Upload progress */}
            {uploading && (
                <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 13, color: '#555', marginBottom: 6 }}>
                        Uploading... {progress}%
                    </div>
                    <div style={{ height: 6, background: '#eee', borderRadius: 3 }}>
                        <div style={{
                            height: '100%', borderRadius: 3,
                            background: '#1a6b3c',
                            width: `${progress}%`,
                            transition: 'width 0.3s ease',
                        }} />
                    </div>
                    <div style={{ fontSize: 12, color: '#888', marginTop: 6 }}>
                        File will be transcoded automatically after upload.
                    </div>
                </div>
            )}

            <div style={adStyles.formActions}>
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button onClick={handleSubmit} disabled={uploading}>
                    {uploading ? 'Uploading...' : 'Upload'}
                </Button>
            </div>
        </>
    );
}

// ── Edit Form ─────────────────────────────────────────────────────────────────

function EditContentForm({ item, onSaved, onClose }: {
    item: ContentItem;
    onSaved: () => void;
    onClose: () => void;
}) {
    const [title, setTitle]         = useState(item.title);
    const [weight, setWeight]       = useState(item.weightSeconds);
    const [duration, setDuration]   = useState(item.durationSeconds);
    const [activeFrom, setFrom]     = useState(item.activeFrom?.split('T')[0] ?? '');
    const [activeTo, setTo]         = useState(item.activeTo?.split('T')[0] ?? '');
    const [isActive, setIsActive]   = useState(item.isActive);
    const [saving, setSaving]       = useState(false);
    const [error, setError]         = useState('');

    const handleSubmit = async () => {
        if (!title.trim()) { setError('Title is required'); return; }
        setSaving(true);
        setError('');
        try {
            await contentApi.update(item.id, {
                title: title.trim(),
                weightSeconds: weight,
                durationSeconds: duration,
                activeFrom: activeFrom || undefined,
                activeTo: activeTo || undefined,
                isActive,
            });
            onSaved();
        } catch (e: any) {
            setError(e.message ?? 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            {error && <div style={adStyles.error}>{error}</div>}
            <FormField label="Title">
                <input style={inputStyle} value={title}
                    onChange={e => setTitle(e.target.value)} autoFocus />
            </FormField>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <FormField label="Duration (seconds)">
                    <input style={inputStyle} type="number" value={duration}
                        onChange={e => setDuration(parseInt(e.target.value) || 15)} />
                </FormField>
                <FormField label="Playback Weight">
                    <input style={inputStyle} type="number" value={weight}
                        onChange={e => setWeight(parseInt(e.target.value) || 60)} />
                </FormField>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <FormField label="Active From">
                    <input style={inputStyle} type="date" value={activeFrom}
                        onChange={e => setFrom(e.target.value)} />
                </FormField>
                <FormField label="Active To">
                    <input style={inputStyle} type="date" value={activeTo}
                        onChange={e => setTo(e.target.value)} />
                </FormField>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8,
                fontSize: 14, cursor: 'pointer', marginBottom: 16 }}>
                <input type="checkbox" checked={isActive}
                    onChange={e => setIsActive(e.target.checked)} />
                Active (shows in channel)
            </label>
            <div style={adStyles.formActions}>
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button onClick={handleSubmit} disabled={saving}>
                    {saving ? 'Saving...' : 'Save Changes'}
                </Button>
            </div>
        </>
    );
}

// ── Content Row ───────────────────────────────────────────────────────────────

function ContentRow({ item, clubId, onUpdated }: {
    item: ContentItem;
    clubId: string | null;
    onUpdated: () => void;
}) {
    const [showEdit, setShowEdit]       = useState(false);
    const [deleting, setDeleting]       = useState(false);
    const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [status, setStatus] = useState<TranscodeStatus>(item.transcodeStatus);

    // Poll for transcode completion
    useEffect(() => {
        if (status !== 'Pending' && status !== 'Processing') return;
            pollTimerRef.current = setTimeout(async () => {
            try {
                const updated = await contentApi.getStatus(item.id);
                setStatus(updated.transcodeStatus as TranscodeStatus);
                if (updated.transcodeStatus === 'Complete' || updated.transcodeStatus === 'Failed') {
                    onUpdated();
                }
            } catch { /* ignore */ }
        }, 5000);
        return () => {
            if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status]);

    const handleDelete = async () => {
        if (!window.confirm(`Delete "${item.title}"? This cannot be undone.`)) return;
        setDeleting(true);
        try {
            await contentApi.delete(item.id);
            onUpdated();
        } catch (e: any) {
            alert(e.message ?? 'Delete failed');
        } finally {
            setDeleting(false);
        }
    };

    return (
        <>
            <div style={adStyles.row}>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <span style={{ fontSize: 14, fontWeight: 600 }}>{item.title}</span>
                        {!item.isActive && (
                            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
                                background: '#f0f0f0', color: '#888', padding: '2px 6px',
                                borderRadius: 3 }}>INACTIVE</span>
                        )}
                    </div>
                    <div style={{ fontSize: 12, color: '#888', display: 'flex', gap: 12,
                        flexWrap: 'wrap' }}>
                        <span>{item.durationSeconds}s</span>
                        {item.weightSeconds !== 60 && <span>weight {item.weightSeconds}</span>}
                        {item.fileSizeBytes && <span>{formatBytes(item.fileSizeBytes)}</span>}
                        {item.impressions30d > 0 && (
                            <span style={{ color: '#1a6b3c', fontWeight: 600 }}>
                                {item.impressions30d} plays (30d)
                            </span>
                        )}
                        {item.activeFrom && (
                            <span>From {new Date(item.activeFrom).toLocaleDateString('en-AU')}</span>
                        )}
                        {item.activeTo && (
                            <span>To {new Date(item.activeTo).toLocaleDateString('en-AU')}</span>
                        )}
                    </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                    <span style={{
                        ...statusBadge(status),
                        fontSize: 11, fontWeight: 700, padding: '3px 8px',
                        borderRadius: 4, letterSpacing: 0.5,
                    }}>
                        {status === 'Pending' || status === 'Processing'
                            ? `⟳ ${status}` : status}
                    </span>
                    <Button size="sm" variant="ghost"
                        onClick={() => setShowEdit(true)}
                        disabled={status === 'Pending' || status === 'Processing'}>
                        Edit
                    </Button>
                    <Button size="sm" variant="danger"
                        onClick={handleDelete} disabled={deleting}>
                        {deleting ? '...' : 'Delete'}
                    </Button>
                </div>
            </div>

            {showEdit && (
                <Modal title="Edit Content" onClose={() => setShowEdit(false)}>
                    <EditContentForm item={item}
                        onSaved={() => { setShowEdit(false); onUpdated(); }}
                        onClose={() => setShowEdit(false)} />
                </Modal>
            )}
        </>
    );
}

// ── Section ───────────────────────────────────────────────────────────────────

function ContentSection({ title, hint, clubId, adType, sponsors, items, onUpdated }: {
    title: string;
    hint: string;
    clubId: string | null;
    adType: AdType;
    sponsors?: { id: string; name: string }[];
    items: ContentItem[];
    onUpdated: () => void;
}) {
    const [showUpload, setShowUpload] = useState(false);

    return (
        <>
            <div style={adStyles.section}>
                <div style={adStyles.sectionHeader}>
                    <div>
                        <div style={adStyles.sectionTitle}>{title}</div>
                        <div style={adStyles.sectionHint}>{hint}</div>
                    </div>
                    <Button size="sm" onClick={() => setShowUpload(true)}>+ Upload</Button>
                </div>

                {items.length === 0 ? (
                    <div style={adStyles.empty}>No {title.toLowerCase()} uploaded yet.</div>
                ) : (
                    <div>
                        {items.map(item => (
                            <ContentRow key={item.id} item={item}
                                clubId={clubId} onUpdated={onUpdated} />
                        ))}
                    </div>
                )}
            </div>

            {showUpload && (
                <Modal title={`Upload ${title}`} onClose={() => setShowUpload(false)}>
                    <UploadForm
                        clubId={clubId}
                        adType={adType}
                        sponsors={sponsors}
                        onUploaded={() => { setShowUpload(false); onUpdated(); }}
                        onClose={() => setShowUpload(false)} />
                </Modal>
            )}
        </>
    );
}

// ── Main AdvertsTab ───────────────────────────────────────────────────────────

export default function AdvertsTab({ clubId, clubSlug, isSuperUser, sponsors }: AdvertsTabProps) {
    const [items, setItems]     = useState<ContentItem[]>([]);
    const [platform, setPlatform] = useState<ContentItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError]     = useState('');

    const load = async () => {
        setLoading(true);
        setError('');
        try {
            const clubItems = await contentApi.list(clubId);
            setItems(clubItems);

            if (isSuperUser) {
                const platformItems = await contentApi.list(null);
                setPlatform(platformItems);
            }
        } catch (e: any) {
            setError(e.message ?? 'Failed to load content');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [clubId]); // eslint-disable-line react-hooks/exhaustive-deps

    if (loading) return <div style={adStyles.centre}>Loading...</div>;
    if (error)   return <div style={adStyles.errorBanner}>{error}</div>;

    const of = (type: string) => items.filter(i => i.type === type);

    return (
        <div>
            {/* SuperUser — Platform Ads */}
            {isSuperUser && (
                <>
                    <div style={adStyles.platformHeader}>
                        <span style={adStyles.platformBadge}>19thView Platform</span>
                        <span style={{ fontSize: 13, color: '#666' }}>
                            Platform ads appear across all clubs at the configured ratio
                        </span>
                    </div>
                    <ContentSection
                        title="Platform Ads"
                        hint="Shown across all clubs. Managed by 19thView SuperUsers only."
                        clubId={null}
                        adType="PlatformAd"
                        items={platform.filter(i => i.type === 'PlatformAd')}
                        onUpdated={load}
                    />
                    <div style={adStyles.divider} />
                </>
            )}

            {/* Club Local Ads */}
            <ContentSection
                title="Local Ads"
                hint="Club-sold advertising. Plays in the main ad break rotation."
                clubId={clubId}
                adType="LocalAd"
                items={of('LocalAd')}
                onUpdated={load}
            />

            {/* Club Sponsor Ads */}
            <ContentSection
                title="Sponsor Ads"
                hint="Competition sponsor video ads. Higher priority than local ads."
                clubId={clubId}
                adType="SponsorAd"
                sponsors={sponsors}
                items={of('SponsorAd')}
                onUpdated={load}
            />

            {/* Club Notices */}
            <ContentSection
                title="Club Notices"
                hint="Announcements, upcoming events, and notifications. Upload an image, PDF, or type a quick text notice."
                clubId={clubId}
                adType="ClubNotice"
                items={of('ClubNotice')}
                onUpdated={load}
            />

            {/* Beauty Loop */}
            <ContentSection
                title="Beauty Loop"
                hint="Ambient golf footage shown during idle periods between ads and clips."
                clubId={clubId}
                adType="BeautyLoop"
                items={of('BeautyLoop')}
                onUpdated={load}
            />
        </div>
    );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const adStyles: Record<string, React.CSSProperties> = {
    section: {
        background: '#fff', borderRadius: 12,
        border: '1.5px solid #eee', padding: 24, marginBottom: 16,
    },
    sectionHeader: {
        display: 'flex', alignItems: 'flex-start',
        justifyContent: 'space-between', marginBottom: 12, gap: 16,
    },
    sectionTitle: { fontSize: 15, fontWeight: 700, marginBottom: 2 },
    sectionHint:  { fontSize: 12, color: '#888' },
    row: {
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '12px 0', borderBottom: '1px solid #f0f0f0',
    },
    dropZone: {
        border: '2px dashed #ccc', borderRadius: 8,
        padding: 24, cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        minHeight: 100, transition: 'all 0.2s',
    },
    empty: { color: '#999', fontSize: 14, padding: '16px 0', textAlign: 'center' },
    centre: { padding: 40, textAlign: 'center', color: '#888' },
    error: {
        background: '#fff0f0', color: '#c00', border: '1px solid #fcc',
        borderRadius: 8, padding: '12px 16px', fontSize: 14, marginBottom: 16,
    },
    errorBanner: {
        background: '#fff0f0', color: '#c00', border: '1px solid #fcc',
        borderRadius: 8, padding: '12px 16px', fontSize: 14,
    },
    formActions: {
        display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16,
        position: 'sticky', bottom: 0, background: '#fff',
        paddingTop: 14, paddingBottom: 4, borderTop: '1px solid #eee',
        marginLeft: -24, marginRight: -24, paddingLeft: 24, paddingRight: 24,
    },
    platformHeader: {
        display: 'flex', alignItems: 'center', gap: 12,
        marginBottom: 12, padding: '10px 16px',
        background: '#f0f7f3', borderRadius: 8,
        border: '1px solid #c8e0d2',
    },
    platformBadge: {
        fontSize: 11, fontWeight: 800, letterSpacing: 0.5,
        background: '#1a6b3c', color: '#fff',
        padding: '3px 10px', borderRadius: 4,
    },
    divider: {
        height: 1, background: '#eee', margin: '4px 0 20px',
    },
};
